import { Button } from "@svea/design-system-components-react";
import { useEffect, useId, useState } from "react";
import { Field, useFormState } from "react-final-form";
import { useText } from "../../../hooks";
import type { Country } from "../../../types/country";
import type { QuestionnaireFormValues } from "../../../types/form";
import type { LangKey } from "../../../types/lang";
import type { CountryAndTinAnswer, CountryAndTinQuestion } from "../../../types/question";
import { convertCountriesToSelectOptions } from "../../../utils/country";
import { validateCountryAndTin } from "../../../utils/validation";
import { Fieldset, FormHelperText, Legend, ReactSelect } from "../../form-fields";
import { TextInput } from "../../form-fields/components/text-input";
import { Stack } from "../../stack";
import { Text } from "../../typography";
import {
	formatFieldName,
	getCountryNameByCode,
	updateCountryTin,
	updateTinCountryList,
} from "../utils";

type Props = {
	question: CountryAndTinQuestion;
	countries: Array<Country>;
	isMultiSelect?: boolean;
};

const CountryAndTin = ({ question, countries, isMultiSelect = false }: Props) => {
	// todo: accept lang object as prop instead of using useText?
	const countryOptions = convertCountriesToSelectOptions(countries);
	const describedById = useId();
	const labeledById = useId();

	const [hasSubmitFailed, setHasSubmitError] = useState(false);
	const { question: questionText } = question;
	const [t, texts] = useText();

	const fieldValues =
		useFormState<QuestionnaireFormValues<CountryAndTinAnswer>>().values[
			formatFieldName(question.questionId)
		] || [];

	const currentCountryValues = countryOptions.filter((o) =>
		fieldValues.some((f) => f.answerValue.alpha2Code === o.value),
	);

	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			<Legend id={labeledById}>{questionText}</Legend>
			<Stack gap={2}>
				<Field<Array<CountryAndTinAnswer>>
					name={formatFieldName(question.questionId)}
					format={(v) => v || []} // defaults the field to empty array instead of empty string
					validate={validateCountryAndTin} // todo: not a hard coded validation fn?
				>
					{({ input, meta }) => {
						// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
						useEffect(() => {
							setHasSubmitError(!!meta.submitFailed && !!meta.error);
						}, [meta]);
						return (
							<Stack gap={2}>
								{isMultiSelect ? (
									<ReactSelect
										labeledBy={labeledById}
										options={countryOptions}
										isMulti
										menuPortalTarget={null}
										placeholder="Sök"
										controlShouldRenderValue={false}
										hideSelectedOptions={false}
										value={currentCountryValues}
										onChange={(value) => {
											if (value)
												input.onChange(
													updateTinCountryList(
														value.map((a) => a.value),
														input.value,
													),
												);
										}}
									/>
								) : (
									<ReactSelect
										menuPortalTarget={null}
										value={currentCountryValues[0]}
										options={countryOptions}
										placeholder="Sök"
										controlShouldRenderValue={false}
										label={t(texts.common.choose)}
										onChange={(option) => {
											if (option) input.onChange(updateTinCountryList([option.value], []));
										}}
									/>
								)}

								{input.value.length > 0 && (
									<>
										<Text>{t(texts.kyc.beneficialOwnerQuestions.taxNumberFor)}</Text>
										<Stack gap={1}>
											{input.value.map(({ answerValue: { alpha2Code } }) => (
												<Stack gap={4} direction="row" key={alpha2Code}>
													<TextInput
														value={
															fieldValues.find((v) => v.answerValue.alpha2Code === alpha2Code)
																?.answerValue.tin
														}
														label={getCountryNameByCode(alpha2Code, countries)}
														onChange={(e) => {
															input.onChange(
																updateCountryTin(alpha2Code, input.value, e.target.value),
															);
														}}
													/>
													{isMultiSelect && (
														<Button
															aria-label="Ta bort"
															variant="icon"
															icon="trash"
															onPress={() => {
																input.onChange(
																	updateTinCountryList(
																		currentCountryValues
																			.filter((c) => c.value !== alpha2Code)
																			.map((a) => a.value),
																		input.value,
																	),
																);
															}}
														/>
													)}
												</Stack>
											))}
										</Stack>
									</>
								)}
								{meta.submitFailed && (
									<FormHelperText error={!!meta.error && meta.submitFailed} id={describedById}>
										{t(meta.error as LangKey)}
									</FormHelperText>
								)}
							</Stack>
						);
					}}
				</Field>
			</Stack>
		</Fieldset>
	);
};
export default CountryAndTin;
